import { Component, inject, signal, effect, computed, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { SatisfactionService, Feedback, FeedbackResponse } from '../../core/services/satisfaction.service';
import { MessageTemplateService, MessageTemplate } from '../../core/services/message-template.service';
import { SessionService } from '../../core/session.service';
import { AuthService } from '../../auth/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { TimezoneService } from '../../core/services/timezone.service';
import { FeedbackSelectionService } from '../../core/services/feedback-selection.service';
import { CorrectiveActionsService, CorrectiveAction, CreateActionRequest, UpdateActionRequest, ActionsResponse } from '../../core/services/corrective-actions.service';
import { formatInTimeZone } from 'date-fns-tz';
import { CallModalComponent } from './call-modal/call-modal.component';
import { ReplyModalComponent } from './reply-modal/reply-modal.component';
import { BulkReplyModalComponent } from './bulk-reply-modal/bulk-reply-modal.component';
import { FeedbacksTableComponent } from './feedbacks-table/feedbacks-table.component';
import { FeedbacksCardsComponent } from './feedbacks-cards/feedbacks-cards.component';
import { ReplyHistoryModalComponent } from './reply-history-modal/reply-history-modal.component';
import { CallHistoryModalComponent } from './call-history-modal/call-history-modal.component';

// DEPRECATED (2026-09-08) : décision produit de retirer les actions correctives de Satisfaction.
// Front masqué (commentaires HTML, réversible) : bouton "Voir les actions" (déjà invisible),
// filtre "Actions en cours", bouton action des cartes (4 groupes) + badge/statut, colonne "Action"
// du tableau, et les 3 modales create/edit/actions-list-modal (imports retirés ci-dessus).
// Le code TS ci-dessous (état + méthodes "Actions correctives", CorrectiveActionsService) reste en
// place mais devient inatteignable depuis l'UI -- conservé pour faciliter un retour arrière, à
// supprimer avec le reste au moment de la décommission définitive. Voir aussi, côté
// waitora-backend : CorrectiveActionController, CorrectiveActionService/Repository/Port (module
// waitora-quality), domaine CorrectiveAction, table quality.corrective_actions.
// NE PAS CONFONDRE avec CorrectiveActionTemplate (catalogue de modèles) : cette référentielle est
// partagée avec Qualité et le simulateur app mobile (component/message + action-template-modal),
// hors périmètre de cette dépréciation.
//
// DEPRECATED (2026-09-08) : décision produit de retirer l'alerte qualité, partout dans le front
// (cette page ET le bouton "Action requise" de la top bar, shell.component.ts/html). Retirés ici :
// <app-quality-alert> (import ci-dessus), les appels à checkQualityAlert() (constructeur,
// onCreateAction, handleArchiveFeedback -- méthode et signaux showQualityAlert/alertMessage/
// criticalCount conservés mais inatteignables). Côté waitora-backend : endpoint GET
// /api/v1/satisfaction/establishments/{id}/quality-alert (SatisfactionController.checkQualityAlert)
// et PatientFeedbackRepository.countCriticalFeedbacks deviennent orphelins. IMPORTANT : ça libère
// aussi hasActiveAction/getLastActionDate/getLastCompletedActionDate de CorrectiveActionService,
// que le bandeau "Actions correctives" ci-dessus marquait "à garder" -- ce n'était plus vrai que
// pour cette alerte, elle-même retirée maintenant. Voir la mise à jour de ces bandeaux côté backend.
@Component({
  selector: 'app-satisfaction',
  standalone: true,
  imports: [CommonModule, FormsModule, CallModalComponent, ReplyModalComponent, BulkReplyModalComponent, FeedbacksTableComponent, FeedbacksCardsComponent, ReplyHistoryModalComponent, CallHistoryModalComponent, TranslatePipe],
  templateUrl: './satisfaction.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./satisfaction.component.scss']
})
export class SatisfactionComponent {
  @Input() inline = false;

  @Input() set initialFeedbackId(id: string | null) {
    if (id) this.highlightFeedbackId.set(id);
  }

  private api = inject(SatisfactionService);
  private templateService = inject(MessageTemplateService);
  session = inject(SessionService);
  private toastService = inject(ToastService);
  private timezone = inject(TimezoneService);
  private selectionService = inject(FeedbackSelectionService);
  private actionsService = inject(CorrectiveActionsService);
  private auth = inject(AuthService);

  /**
   * Vue agrégée "tous les sites" : réservée aux clients avec le droit Waitora V2 (SUPERVISION),
   * et uniquement hors intégration /site (qui reste toujours mono-site).
   */
  private useOrgWideView(): boolean {
    return !this.inline && this.auth.hasPermission('SUPERVISION');
  }

  loading = signal<boolean>(false);
  sorting = signal<boolean>(false);
  error = signal<string | null>(null);
  data = signal<FeedbackResponse | null>(null);
  currentPage = signal<number>(0);
  pageSize = signal<number>(20);

  showPendingOnly = signal<boolean>(false);
  activeFilter = signal<'none' | 'recontact' | 'actions-ongoing'>('none');
  siteSearchQuery = signal<string>('');

  // Alerte qualité
  showQualityAlert = signal<boolean>(false);
  alertMessage = signal<string>('');
  criticalCount = signal<number>(0);
  useCardsView = signal<boolean>(true);
  showTreated = signal<boolean>(true);

  toggleTreatedView() {
    this.showTreated.set(!this.showTreated());
  }

  // Actions correctives
  actions = signal<CorrectiveAction[]>([]);
  actionsCount = signal<number>(0);
  actionsData = signal<ActionsResponse | null>(null);
  showActionsListModal = signal<boolean>(false);
  showCreateActionModal = signal<boolean>(false);
  showEditActionModal = signal<boolean>(false);
  selectedAction = signal<CorrectiveAction | null>(null);
  selectedActionEstablishmentId = signal<string | null>(null);
  highlightFeedbackId = signal<string | null>(null);

  constructor() {
    effect(() => {
      const estId = this.session.selectedId();
      const establishments = this.session.establishments();
      if (!this.useOrgWideView()) {
        // Site unique (comportement historique) : /site, ou page seule sans droit Waitora V2
        if (estId) {
          this.loadFeedbacks(estId);
          // DEPRECATED (2026-09-08) : appel retiré avec l'alerte qualité -- voir la note en tête
          // de cette classe.
          // this.checkQualityAlert(estId);
        }
      } else {
        // Page seule, avec droit Waitora V2 : tous les sites confondus
        if (establishments.length > 0) {
          this.loadAllFeedbacks(establishments.map(e => e.id));
          // DEPRECATED (2026-09-08) : voir la note ci-dessus.
          // if (estId) this.checkQualityAlert(estId);
        }
      }
    });
  }

  private loadFeedbacks(establishmentId: string) {
    this.loading.set(true);
    this.error.set(null);

    this.api.getFeedbacks(establishmentId).subscribe({
      next: (response) => {
        this.data.set(response);
        this.updateSortedFeedbacks();
        this.loading.set(false);
      },
      error: (err) => {
        const sanitizedError = err?.message?.replace(/[\r\n]/g, ' ') || 'Unknown error';
        console.error('Feedback loading failed:', sanitizedError);
        this.error.set('Erreur lors du chargement des feedbacks');
        this.loading.set(false);
      }
    });
  }

  private loadAllFeedbacks(establishmentIds: string[]) {
    this.loading.set(true);
    this.error.set(null);

    const establishments = this.session.establishments();
    this.api.getOrgFeedbacks().subscribe({
      next: (responseByEstablishmentId) => {
        const allFeedbacks = establishmentIds.flatMap(id => {
          const response = responseByEstablishmentId[id];
          if (!response) return [];
          const estName = establishments.find(e => e.id === id)?.name || '';
          return response.feedbacks.map(f => ({ ...f, establishment_id: id, establishment_name: estName }));
        });
        const totalActions = establishmentIds.reduce((sum, id) => sum + (responseByEstablishmentId[id]?.counts?.total_actions || 0), 0);
        const totalArchived = establishmentIds.reduce((sum, id) => sum + (responseByEstablishmentId[id]?.counts?.archived_feedbacks || 0), 0);
        const merged: FeedbackResponse = {
          feedbacks: allFeedbacks,
          counts: { total_actions: totalActions, archived_feedbacks: totalArchived }
        } as FeedbackResponse;
        this.data.set(merged);
        this.updateSortedFeedbacks();
        this.loading.set(false);
      },
      error: (err) => {
        const sanitizedError = err?.message?.replace(/[\r\n]/g, ' ') || 'Unknown error';
        console.error('Feedback loading failed:', sanitizedError);
        this.error.set('Erreur lors du chargement des feedbacks');
        this.loading.set(false);
      }
    });
  }

  private refreshFeedbacks() {
    const estId = this.session.selectedId();
    if (this.useOrgWideView()) {
      const establishments = this.session.establishments();
      if (establishments.length > 0) {
        this.loadAllFeedbacks(establishments.map(e => e.id));
      }
    } else if (estId) {
      this.loadFeedbacks(estId);
    }
  }

  // Actions correctives
  private loadActions(establishmentId: string) {
    this.actionsService.getActions(establishmentId).subscribe({
      next: (response) => {
        this.actions.set(response.actions);
        this.actionsCount.set(response.counts.total);
        this.actionsData.set(response);
      },
      error: (err) => {
        console.error('Actions loading failed: [Error details sanitized for security]');
      }
    });
  }

  openActionsListModal() {
    // La modale se charge elle-même à l'ouverture
    this.showActionsListModal.set(true);
  }

  closeActionsListModal() {
    this.showActionsListModal.set(false);
  }

  openCreateActionModal() {
    this.showCreateActionModal.set(true);
    // Use Angular's Renderer2 or CSS classes instead of direct DOM manipulation
  }

  closeCreateActionModal() {
    this.showCreateActionModal.set(false);
    // Use Angular's Renderer2 or CSS classes instead of direct DOM manipulation
  }

  /**
   * En vue "tous les établissements", session.selectedId() ne reflète que le site choisi
   * dans la sidebar, qui peut être différent de celui du feedback concerné.
   * On résout donc l'établissement réel à partir du feedback lui-même.
   */
  private getFeedbackEstablishmentId(feedback: Feedback | null | undefined): string | null {
    if (this.useOrgWideView() && feedback?.establishment_id) {
      return feedback.establishment_id;
    }
    return this.session.selectedId();
  }

  getCreateActionEstablishmentId(): string | null {
    return this.getFeedbackEstablishmentId(this.selectedFeedbacks()[0]);
  }

  onCreateAction(actionData: CreateActionRequest) {
    const estId = this.getCreateActionEstablishmentId();
    if (!estId) return;

    const feedbackIds = this.getSelectedFeedbackIds();

    this.actionsService.createAction(estId, actionData).subscribe({
      next: (response) => {
        this.toastService.show('Action créée avec succès', 'success', 4000, response.message || `Action ${response.data.action_id.slice(0, 8)} créée et assignée`);
        this.loadActions(estId);
        this.refreshFeedbacks();
        this.checkQualityAlert(estId);
        
        if (feedbackIds.length > 0) {
          this.highlightFeedbackId.set(feedbackIds[0]);
        }
        
        this.clearSelection();
        this.closeCreateActionModal();
      },
      error: (err) => {
        const errorMessage = err?.error?.message || err?.message || 'Erreur lors de la création de l\'action';
        this.toastService.show('Erreur de création', 'error', 4000, errorMessage);
      }
    });
  }

  onActionUpdated() {
    // La modale gère son propre rechargement
    // Plus besoin de loadActions ici
  }

  onEditAction(action: CorrectiveAction) {
    this.selectedAction.set(action);
    this.selectedActionEstablishmentId.set(action.establishment_id || this.session.selectedId());
    this.showEditActionModal.set(true);
  }

  closeEditActionModal() {
    this.showEditActionModal.set(false);
    this.selectedAction.set(null);
    this.selectedActionEstablishmentId.set(null);
    // Le rafraîchissement se fait maintenant dans onUpdateAction
  }

  onUpdateAction(data: {actionId: string, updates: UpdateActionRequest}) {
    this.actionsService.updateAction(data.actionId, data.updates).subscribe({
      next: (response) => {
        this.toastService.show('Action sauvegardée', 'success', 3000, 'Action mise à jour avec succès');
        // Mettre à jour l'action sélectionnée avec la réponse
        this.selectedAction.set(response);
        this.closeEditActionModal();

        // Recharger les feedbacks pour mettre à jour le statut
        this.refreshFeedbacks();

        this.onActionUpdated(); // Rafraîchir après fermeture
      },
      error: (err) => {
        console.error('Action update failed:', err);
        const errorMessage = err?.error?.message || err?.message || 'Erreur lors de la mise à jour';
        this.toastService.show('Erreur lors de la mise à jour', 'error', 3000, errorMessage);
      }
    });
  }

  onCreateSingleAction(feedback: Feedback) {
    this.selectionService.clear();
    this.selectionService.forceSelect(feedback);
    this.openCreateActionModal();
  }

  getSelectedFeedbackIds(): string[] {
    return this.selectedFeedbacks().map(f => f.feedback_id);
  }

  // Actions correctives - Formulaire simple
  actionForm = {
    type: 'corrective' as 'curative' | 'corrective',
    trigger_reason: '',
    description: '',
    responsible: '',
    date: new Date().toISOString().slice(0, 16),
    status: 'pending' as 'pending' | 'in_progress' | 'completed' | 'cancelled'
  };

  selectedActionTemplate = '';

  getNextActionNumber(): number {
    const actions = this.actions();
    if (actions.length === 0) return 1;

    const maxNumber = Math.max(...actions.map(a => (a as any).number || 0));
    return maxNumber + 1;
  }

  createAction() {
    if (!this.actionForm.description.trim()) return;

    const actionData: CreateActionRequest = {
      description: this.actionForm.description,
      trigger_reason: this.actionForm.trigger_reason || 'Autre cause identifiée',
      is_corrective: this.actionForm.type === 'corrective'
    };

    this.onCreateAction(actionData);
    this.resetActionForm();
  }

  private resetActionForm() {
    this.actionForm = {
      type: 'corrective',
      trigger_reason: '',
      description: '',
      responsible: '',
      date: new Date().toISOString().slice(0, 16),
      status: 'pending'
    };
    this.selectedActionTemplate = '';
  }

  private checkQualityAlert(establishmentId: string) {
    this.api.getQualityAlert(establishmentId).subscribe({
      next: (alert) => {
        this.showQualityAlert.set(alert.action_required);
        this.alertMessage.set(alert.message);
        this.criticalCount.set(alert.critical_feedbacks_count);
      },
      error: (err) => {
        const sanitizedError = err?.message?.replace(/[\r\n]/g, ' ') || 'Unknown error';
        console.error('Quality alert check failed:', sanitizedError);
      }
    });
  }

  // Méthodes de pagination supprimées - plus nécessaires

  sortField = signal<string>('');
  sortDirection = signal<'asc' | 'desc'>('desc');

  sortedFeedbacks = signal<Feedback[]>([]);

  pendingCount = computed(() => {
    const data = this.data();
    if (!data) return 0;
    return data.feedbacks.filter(f => f.wants_reply && f.contact_email && !f.replied_at).length;
  });

  filteredFeedbacks = computed(() => {
    const sorted = this.sortedFeedbacks();
    const filter = this.activeFilter();
    const siteQuery = this.siteSearchQuery().toLowerCase().trim();
    
    let filtered = sorted;

    if (siteQuery) {
      filtered = filtered.filter(f => f.establishment_name?.toLowerCase().includes(siteQuery));
    }
    
    if (this.showPendingOnly()) {
      filtered = filtered.filter(f => f.wants_reply && f.contact_email && !f.replied_at);
    }
    
    if (filter === 'recontact') {
      filtered = filtered.filter(f => f.wants_reply && !f.replied_at);
    } else if (filter === 'actions-ongoing') {
      filtered = filtered.filter(f => f.has_corrective_action && f.corrective_action_status === 'in_progress');
    }
    
    return filtered;
  });

  togglePendingFilter() {
    this.showPendingOnly.set(!this.showPendingOnly());
  }

  recontactCount = computed(() => {
    const data = this.data();
    if (!data) return 0;
    return data.feedbacks.filter(f => f.wants_reply && !f.replied_at).length;
  });

  ongoingActionsCount = computed(() => {
    const data = this.data();
    if (!data) return 0;
    return data.feedbacks.filter(f => f.has_corrective_action && f.corrective_action_status === 'in_progress').length;
  });

  setFilter(filter: 'recontact' | 'actions-ongoing') {
    if (this.activeFilter() === filter) {
      this.activeFilter.set('none');
    } else {
      this.activeFilter.set(filter);
    }
  }

  clearFilter() {
    this.activeFilter.set('none');
  }

  sortBy(field: string) {
    if (this.sortField() === field) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortDirection.set('desc');
    }
    this.updateSortedFeedbacks();
  }

  private updateSortedFeedbacks() {
    const data = this.data();
    if (!data) return;

    if (!this.sortField()) {
      this.sortedFeedbacks.set([...data.feedbacks]);
      return;
    }

    const sorted = [...data.feedbacks].sort((a, b) => {
      const field = this.sortField();
      const direction = this.sortDirection();

      let aVal: string | number, bVal: string | number;

      switch (field) {
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'queue_name':
          aVal = (a.queue_name || '').toLowerCase();
          bVal = (b.queue_name || '').toLowerCase();
          break;
        case 'score':
          aVal = a.score || 0;
          bVal = b.score || 0;
          break;
        case 'reported_bucket':
          aVal = a.reported_bucket || 0;
          bVal = b.reported_bucket || 0;
          break;
        case 'contact_email':
          aVal = a.contact_email || '';
          bVal = b.contact_email || '';
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    this.sortedFeedbacks.set(sorted);
  }

  // Getters pour la sélection
  selectedFeedbacks = this.selectionService.selected;

  clearSelection() {
    this.selectionService.clear();
  }
  showBulkReplyModal = false;

  // Modale d'appel
  showCallModal = false;
  showCallHistoryModal = false;
  selectedCallFeedback: Feedback | null = null;

  openBulkReplyModal() {
    this.showBulkReplyModal = true;
  }

  closeBulkReplyModal() {
    this.showBulkReplyModal = false;
  }

  onSendBulkReply(data: {subject: string, message: string}) {
    if (!data.message.trim() || this.selectedFeedbacks().length === 0) return;

    const feedbackIds = this.selectedFeedbacks().map(f => f.feedback_id);

    this.api.sendBulkReplyEmail(feedbackIds, data.subject, data.message).subscribe({
      next: (response) => {
        response.results.forEach(result => {
          if (result.status === 'sent') {
            // Éviter la mutation directe - laisser updateSortedFeedbacks() gérer la synchronisation
          }
        });

        this.updateSortedFeedbacks();
        this.clearSelection();

        if (response.failed === 0) {
          this.toastService.show('C\'est noté. Merci 👋', 'success', 4000, `${response.success} emails envoyés avec succès`);
        } else {
          this.toastService.show('C\'est noté. Merci 👋', 'success', 4000, `${response.success} emails envoyés avec succès, ${response.failed} échecs`);
        }

        this.closeBulkReplyModal();
      },
      error: (err) => {
        const sanitizedError = err?.message?.replace(/[\r\n]/g, ' ') || 'Unknown error';
        console.error('Bulk reply failed:', sanitizedError);
        let errorMessage = 'Erreur lors de l\'envoi de l\'email';
        if (err.status === 400) errorMessage = 'Erreur serveur, veuillez réessayer';
        if (err.status === 403) errorMessage = 'Accès non autorisé';
        if (err.status === 500) errorMessage = 'Erreur serveur, veuillez réessayer';
        this.toastService.show('Erreur', 'error', 4000, errorMessage);
      }
    });
  }

  showReplyModal = false;
  showReplyHistoryModal = false;
  selectedFeedback: Feedback | null = null;

  openReplyModal(feedback: Feedback) {
    if (feedback.replied_at) {
      this.selectedFeedback = feedback;
      this.showReplyHistoryModal = true;
    } else {
      this.selectedFeedback = feedback;
      this.showReplyModal = true;
    }
  }

  closeReplyHistoryModal() {
    this.showReplyHistoryModal = false;
    this.selectedFeedback = null;
  }

  closeReplyModal() {
    this.showReplyModal = false;
    this.selectedFeedback = null;
    document.body.classList.remove('modal-open');
  }

  onSendReply(data: {subject: string, message: string}) {
    if (!this.selectedFeedback) return;

    this.api.sendReplyEmail(
      this.selectedFeedback.feedback_id,
      data.subject,
      data.message
    ).subscribe({
      next: (response) => {
        this.refreshFeedbacks();
        this.toastService.show('C\'est noté. Merci 👋', 'success', 4000, response?.message || 'Email envoyé avec succès');
        this.closeReplyModal();
      },
      error: (err) => {
        const sanitizedError = err?.message?.replace(/[\r\n]/g, ' ') || 'Unknown error';
        console.error('Email send failed:', sanitizedError);
        let errorMessage = 'Erreur lors de l\'envoi de l\'email';
        if (err.status === 400) errorMessage = 'Données invalides pour l\'envoi';
        if (err.status === 403) errorMessage = 'Accès non autorisé';
        if (err.status === 500) errorMessage = 'Erreur serveur, veuillez réessayer';
        this.toastService.show('Erreur', 'error', 4000, errorMessage);
      }
    });
  }

  openCallModal(feedback: Feedback) {
    if (feedback.replied_at) {
      this.selectedCallFeedback = feedback;
      this.showCallHistoryModal = true;
    } else {
      this.selectedCallFeedback = feedback;
      this.showCallModal = true;
      document.body.classList.add('modal-open');
    }
  }

  closeCallModal() {
    this.showCallModal = false;
    this.selectedCallFeedback = null;
    document.body.classList.remove('modal-open');
  }

  closeCallHistoryModal() {
    this.showCallHistoryModal = false;
    this.selectedCallFeedback = null;
  }

  onUpdateCallContent(data: {feedbackId: string, content: string}) {
    this.api.updateCallContent(data.feedbackId, data.content).subscribe({
      next: () => {
        this.refreshFeedbacks();
        this.toastService.show('Contenu mis à jour', 'success', 3000, 'Le contenu du message vocal a été mis à jour');
      },
      error: (err: any) => {
        const errorMessage = err?.error?.message || 'Erreur lors de la mise à jour';
        this.toastService.show('Erreur', 'error', 3000, errorMessage);
      }
    });
  }

  onMarkAsCalled(data: {feedbackId: string, voicemailContent: string}) {
    this.handleMarkAsCalled(data.feedbackId, data.voicemailContent);
    this.closeCallModal();
  }

  handleMarkAsCalled(feedbackId: string, voicemailContent?: string) {
    this.api.markAsCalled(feedbackId, voicemailContent).subscribe({
      next: () => {
        this.refreshFeedbacks();
        this.toastService.show('C\'est noté. Merci 👋', 'success', 4000, 'Patient marqué comme appelé');
      },
      error: (err) => {
        const sanitizedError = err?.message?.replace(/[\r\n]/g, ' ') || 'Unknown error';
        console.error('Mark as called failed:', sanitizedError);
        this.toastService.show('Erreur', 'error', 4000, 'Erreur lors du marquage');
      }
    });
  }

  handleArchiveFeedback(feedbackId: string) {
    this.api.archiveFeedback(feedbackId).subscribe({
      next: (response) => {
        // Supprimer du localStorage
        this.removeFromReadFeedbacks(feedbackId);

        this.refreshFeedbacks();
        // DEPRECATED (2026-09-08) : appel retiré avec l'alerte qualité -- voir la note en tête de
        // cette classe.
        // const estId = this.session.selectedId();
        // if (estId) {
        //   this.checkQualityAlert(estId);
        // }
        this.toastService.show('Feedback archivé', 'success', 3000, response?.message);
      },
      error: (err) => {
        console.error('Archive failed:', err);
        const errorMessage = err?.error?.message || 'Erreur lors de l\'archivage';
        this.toastService.show('Erreur d\'archivage', 'error', 3000, errorMessage);
      }
    });
  }

  private removeFromReadFeedbacks(feedbackId: string) {
    try {
      const userId = this.session.selectedId();
      if (!userId) return;
      
      // Hash simple pour anonymiser l'ID utilisateur
      let hash = 0;
      for (let i = 0; i < userId.length; i++) {
        const char = userId.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      const key = `waitora_read_feedbacks_${Math.abs(hash)}`;
      
      const stored = localStorage.getItem(key);
      if (!stored) return;
      
      const data = JSON.parse(stored);
      if (data.ids && Array.isArray(data.ids)) {
        data.ids = data.ids.filter((id: string) => id !== feedbackId);
        localStorage.setItem(key, JSON.stringify(data));
      }
    } catch (e) {
      console.error('Failed to remove from read feedbacks:', e);
    }
  }

  exportArchivedFeedbacks() {
    const estId = this.session.selectedId();
    if (!estId) return;

    this.api.exportArchivedFeedbacks(estId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        a.download = `feedbacks_${estId}_${timestamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastService.show('Export terminé', 'success', 3000, 'Fichier CSV téléchargé');
      },
      error: (err) => {
        const errorMessage = err?.error?.message || 'Erreur lors de l\'export';
        this.toastService.show('Erreur d\'export', 'error', 3000, errorMessage);
      }
    });
  }

  getEstablishmentName(): string {
    const selectedId = this.session.selectedId();
    const establishment = this.session.establishments().find(e => e.id === selectedId);
    return establishment?.name || 'Établissement';
  }

  openBulkReplyWithSelection(selectedFeedbacks: Feedback[]) {
    this.selectionService.clear();
    selectedFeedbacks.forEach(feedback => {
      this.selectionService.forceSelect(feedback);
    });
    this.openBulkReplyModal();
  }

  onNoReply() {
    const feedback = this.selectedFeedback || this.selectedCallFeedback;
    if (!feedback) return;

    this.api.markAsTreated(feedback.feedback_id).subscribe({
      next: () => {
        this.refreshFeedbacks();
        this.toastService.show('Feedback marqué comme traité', 'success', 3000, 'Le feedback a été marqué comme traité');
        this.closeReplyModal();
        this.closeCallModal();
      },
      error: (err) => {
        const errorMessage = err?.error?.message || 'Erreur lors du marquage';
        this.toastService.show('Erreur', 'error', 3000, errorMessage);
      }
    });
  }

  onViewAction(feedback: Feedback) {
    const estId = this.getFeedbackEstablishmentId(feedback);
    if (!estId) return;

    const feedbackId = feedback.feedback_id;
    this.actionsService.getActions(estId).subscribe({
      next: (response) => {
        const action = response.actions.find(a =>
          a.feedback_ids?.includes(feedbackId) || a.patient_feedback_id === feedbackId
        );
        if (action) {
          this.selectedAction.set(action);
          this.selectedActionEstablishmentId.set(estId);
          this.showEditActionModal.set(true);
        } else {
          this.toastService.show('Action introuvable', 'error', 3000, 'Aucune action trouvée pour ce feedback');
        }
      },
      error: (err) => {
        console.error('Failed to load actions:', err);
        this.toastService.show('Erreur', 'error', 3000, 'Impossible de charger les actions');
      }
    });
  }
}
